import { observer } from "mobx-react-lite";
import IconTuner from "@asphalt-react/icontuner";
import { Cross } from "@asphalt-react/iconpack";
import { Text } from "src/components/Text";
import { Box } from "src/components/Box";
import * as React from "react";
import styled, { css } from "styled-components";
import { FilePickerErrorType, useFilePickerState } from "../states";
import { MediaQueries } from "src/constants";
import { useTranslation } from "src/hooks";
import { Button } from "../../Button";
import { ReactNode } from "react";

const FakeButtonWrapperEl = styled(Box)`
  flex-direction: column;
  z-index: 1;

  ${MediaQueries.screen.medium.minWidth} {
    flex-direction: row;
    ${(props) =>
      props &&
      css`
        &:first-child {
          margin-left: ${props.theme.layout.margin.s}px;
        }

        &:last-child {
          margin-right: ${props.theme.layout.margin.s}px;
        }
      `}
  }
`;

const ErrorIconEl = styled.div`
  & svg {
    height: 80px;
    width: 80px;
  }
`;

const {
  NetworkError,
  FileInvalidType,
  FileMaxSize,
  FileMaxCount,
  FileCompressFailed,
} = FilePickerErrorType;

const DefaultActions = observer(() => {
  const translate = useTranslation();
  const state = useFilePickerState();
  const { retry, deleteFile } = state;

  const onReUploadClick = () => {
    deleteFile();
    setTimeout(() => {
      state.inputRef?.current?.click();
    }, 100);
  };

  return (
    <>
      {state.allowRetries && (
        <Button onClick={retry} paddingLeftXl paddingRightXl>
          {translate("error.cta.retry")}
        </Button>
      )}
      <Button onClick={onReUploadClick} paddingLeftXl paddingRightXl>
        {translate("error.cta.reupload")}
      </Button>
    </>
  );
});

export type IFilePickerErrorProps = {
  getTranslation?: (error: FilePickerErrorType) => string;
  actions?: ReactNode;
};

export const FilePickerError: React.FC<IFilePickerErrorProps> = observer(
  (props) => {
    const translate = useTranslation();
    const state = useFilePickerState();
    const { error, maxSize } = state;
    const { getTranslation, actions } = props;

    const getDefaultTranslation = () => {
      switch (error) {
        case NetworkError:
          return translate("error.message.network_error");
        case FileInvalidType:
          return translate("error.message.file_invalid_type", [`${maxSize}MB`]);
        case FileMaxSize:
          return translate("error.message.file_too_large", [`${maxSize}MB`]);
        case FileMaxCount:
          return translate("error.message.too_many_files");
        case FileCompressFailed:
          return translate("error.message.file_invalid_type", [`${maxSize}MB`]);
        default:
          return "error.message.network_error";
      }
    };

    return (
      <>
        <ErrorIconEl>
          <IconTuner danger>
            <Cross />
          </IconTuner>
        </ErrorIconEl>
        <Text
          color="danger"
          fontWeight="demi"
          fontSize="xsmall"
          marginBottomM
          marginTopS
          textCenter
        >
          {getTranslation ? getTranslation(error!!) : getDefaultTranslation()}
        </Text>
        <Box
          flex
          flexDirectionColumn
          width100
          paddingLeftXl
          paddingRightXl
          marginTopM
        >
          <FakeButtonWrapperEl
            flex
            paddingRightL
            paddingLeftL
            width100
            justifyEvenly
          >
            {actions ?? <DefaultActions />}
          </FakeButtonWrapperEl>
        </Box>
      </>
    );
  }
);
